import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { ICreateAssetGroup, IUpdateAssetGroup } from "./interfaces/asset-group.interface";
import AssetGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/assets/asset-group/asset-group.entity";



const apiUrl = env.API_AVE_ASSET;
const moduleName = "asset-groups";

@Injectable({
  providedIn: "root",
})
export class AssetGroupService extends AbstractService<AssetGroupAttributes, ICreateAssetGroup, IUpdateAssetGroup> {

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Obtiene grupos de activos con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param filter Filters to apply (contains status array)
     * @returns Paginated data
     *
     * Search fields: code, name
     * Array filters: status
     */
    getAllPaginateAsset(
        options: IPagination<AssetGroupAttributes>,
        filter: any
    ): Observable<IPaginationResult<AssetGroupAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtro de estados.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<AssetGroupAttributes>[] => {
            const deletedFilter: FilterFn<AssetGroupAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            return [
                deletedFilter,
                filter?.status && filter.status.length > 0
                    ? andWhere('statusId', Ops.in(...filter.status))
                    : noop()
            ];
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
        const searchByCodeQueryObject: FilterFn<AssetGroupAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
        const searchByNameQueryObject: FilterFn<AssetGroupAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere("name", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<AssetGroupAttributes>(
            // Expandimos ambos objetos en el buildQuery
            ...searchByCodeQueryObject,
            ...searchByNameQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "name",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<AssetGroupAttributes>>(API_URL);
    }

    /**
     * Find to combo box
     * @returns Data
    */
    findToComboBox(): Observable<IRequestResult<AssetGroupAttributes[]>> {
        const query = buildQuery<AssetGroupAttributes>(
            where("statusId", StatusEnum.ACTIVE),
            select(["_id","code", "name"]),
            orderBy("code", OrderVariant.Asc)
        );

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IRequestResult<AssetGroupAttributes[]>>(API_URL);
    }
}
